import { useEffect, useRef } from "react";


/**
 * Hook para cerrar un componente al presionar la tecla Escape
 * @param {Object} props - Propiedades del hook.
 * @param {boolean} props.enabled - Indica si el hook est&aacute; habilitado o no.
 * @param {function} props.onClose - Funci&oacute;n para cerrar el componente.
 * @param {boolean} props.restoreFocus - Indica si se debe restaurar el foco al elemento activo antes de cerrar el componente. (Opcional, por defecto es `true`).
 * @param {React.RefObject} props.fallbackRef - Referencia al elemento activo antes de cerrar el componente. (Opcional, se utiliza si `restoreFocus` es `false`).
 * @returns {Object} Objeto con la referencia al elemento activo antes de cerrar el componente.
 */
export const useEscapeToClose = ({
	enabled,
	onClose,
	restoreFocus = true,
	fallbackRef,
}) => {
	const lastActiveElementRef = useRef(null);

	useEffect(() => {
		if (!enabled) return;

		lastActiveElementRef.current = document.activeElement;
	}, [enabled]);

	useEffect(() => {
		if (!enabled) return;

		const handleEsc = (e) => {
			if (e.key !== "Escape") return;

			onClose();

			if (!restoreFocus) return;

			const target =
				lastActiveElementRef.current ||
				(fallbackRef ? fallbackRef.current : null);

			if (target && typeof target.focus === "function") {
				target.focus();
			}
		};

		document.addEventListener("keydown", handleEsc);

		return () => {
			document.removeEventListener("keydown", handleEsc);
		};
	}, [enabled, onClose, restoreFocus, fallbackRef]);

	return {
		lastActiveElementRef,
	};
};